import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import { mappingsTypes, PpeType } from 'constants/company';

import { selectExtendedUsers, selectExtendedUsersValues } from 'store/users/selectors';

import { getSystem } from './system.selectors';

const selectLendersMappings = createSelector(getSystem, ({ lendersMappings }) => lendersMappings);

const selectCompanyMappings = createSelector(
  selectLendersMappings,
  ({ companyMappings }) => companyMappings,
);

const selectUserMappings = createSelector(
  selectLendersMappings,
  ({ userMappings }) => userMappings,
);

const selectCompanyMappingsValues = createSelector(selectCompanyMappings, (companyMappings = []) =>
  Object.values(companyMappings).map(
    ({
      id,
      target,
      investorIdentity,
      compensationType,
      flatFee,
      compensationPoints,
      isDeleted,
    }) => ({
      id,
      type: target.type,

      investorId: investorIdentity.id,
      investorName: investorIdentity.name,

      compensationType,
      flatFee,
      compensationPoints,
      isDeleted,
    }),
  ),
);

const selectUserMappingsValues = createSelector(
  [selectUserMappings, selectExtendedUsersValues],
  (userMappings = [], users = []) =>
    Object.values(userMappings).map(
      ({
        id,
        target,
        investorIdentity,
        compensationType,
        flatFee,
        compensationPoints,
        isDeleted,
      }) => ({
        id,
        type: target.type,

        userId: target.userId,
        userName: users.find(user => user.id === target.userId)?.name || '',

        investorId: investorIdentity.id,
        investorName: investorIdentity.name,

        compensationType,
        flatFee,
        compensationPoints,
        isDeleted,
      }),
    ),
);

export const selectLendersMappingsValues = memoizeOne(
  ({ mappingType = mappingsTypes.COMPANY_MAPPING }) =>
    createSelector(
      [selectCompanyMappingsValues, selectUserMappingsValues],
      (companyMappings = [], userMappings = []) =>
        mappingType === mappingsTypes.COMPANY_MAPPING ? companyMappings : userMappings,
    ),
);

const selectCompanyMappingsValuesIds = createSelector(
  selectCompanyMappingsValues,
  companyMappingsValues => _.map(companyMappingsValues, 'investorId'),
);

const selectUserMappingsValuesGroupedIds = createSelector(
  selectUserMappingsValues,
  userMappingsValues =>
    userMappingsValues.reduce((acc, { userId, investorId }) => {
      if (!acc[userId]) {
        acc[userId] = [];
      }

      acc[userId].push(investorId);

      return acc;
    }, {}),
);

const selectOptimalBlueInvestors = createSelector(
  selectLendersMappings,
  ({ optimalBlueInvestors }) => optimalBlueInvestors,
);

const selectLoanSifterInvestors = createSelector(
  selectLendersMappings,
  ({ loanSifterInvestors }) => loanSifterInvestors,
);

const valuesToOptions = ({ id, name }) => ({ name, value: id });

const selectOptimalBlueInvestorsOptions = createSelector(
  selectOptimalBlueInvestors,
  (optimalBlueInvestors = []) => Object.values(optimalBlueInvestors).map(valuesToOptions),
);

const selectLoanSifterInvestorsOptions = createSelector(
  selectLoanSifterInvestors,
  (loanSifterInvestors = []) => Object.values(loanSifterInvestors).map(valuesToOptions),
);

export const selectFilteredLendersOptionsByPpeAndMappingType = memoizeOne(
  ({ ppeType, mappingType = mappingsTypes.COMPANY_MAPPING, userId }) =>
    createSelector(
      [
        selectOptimalBlueInvestorsOptions,
        selectLoanSifterInvestorsOptions,
        selectCompanyMappingsValuesIds,
        selectUserMappingsValuesGroupedIds,
      ],
      (
        optimalBlueInvestors = [],
        loanSifterInvestors = [],
        companyMappingsValuesIds = [],
        userMappingsValuesIds = {},
      ) => {
        if (![PpeType.OPTIMAL_BLUE_PPE, PpeType.LOAN_SIFTER_PPE].includes(ppeType)) {
          return [];
        }

        const investorOptions =
          ppeType === PpeType.OPTIMAL_BLUE_PPE ? optimalBlueInvestors : loanSifterInvestors;

        return mappingType === mappingsTypes.COMPANY_MAPPING
          ? investorOptions.filter(({ value }) => !companyMappingsValuesIds.includes(value))
          : investorOptions.filter(
              ({ value }) => !userId || !userMappingsValuesIds[userId]?.includes(value),
            );
      },
    ),
);

export const selectDeletedOrUnassignedUserIds = createSelector(
  [selectUserMappingsValues, selectExtendedUsers],
  (userMappingsValues = [], extendedUsersValues = {}) =>
    userMappingsValues.reduce((acc, { userId }) => {
      if (!extendedUsersValues[userId] && !acc.includes(userId)) {
        acc.push(userId);
      }

      return acc;
    }, []),
);
